import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Snackbar,
  DialogContentText
} from '@mui/material';
import { 
  Delete as DeleteIcon, 
  PersonAdd as PersonAddIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Check as CheckIcon
} from '@mui/icons-material';
import * as customerAccessService from '../../services/customerAccessService';
import * as customerService from '../../services/customerService';
import * as userService from '../../services/userService';
import { useAuth } from '../../context/AuthContext';

export default function CustomerAccessManagement() {
  // State for users, customers, and access permissions
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [accessPermissions, setAccessPermissions] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedAccessUser, setSelectedAccessUser] = useState(null);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Add state for revoke confirmation dialog
  const [revokeConfirmDialog, setRevokeConfirmDialog] = useState({
    open: false,
    accessId: null,
    customerName: ''
  });

  // Get current user info from auth context
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  // Dialog states
  const [showGrantDialog, setShowGrantDialog] = useState(false);
  const [showBulkGrantDialog, setShowBulkGrantDialog] = useState(false);

  // Fetch all users with 'company_user' role
  const loadUsers = useCallback(async () => {
    try {
      // Use different API endpoints based on user role
      const response = isAdmin
        ? await userService.getAllUsers()
        : await userService.getCompanyUsers();
      
      // Handle different response formats based on endpoint
      let userData;
      
      if (isAdmin) {
        // For admin users, check different possible response structures
        userData = Array.isArray(response.data) ? response.data :
                  response.data?.data?.users ? response.data.data.users :
                  response.data?.users ? response.data.users :
                  response.data?.data ? response.data.data : [];
      } else {
        // For company admins, check different possible response structures
        userData = response.data?.users ? response.data.users :
                  Array.isArray(response.data) ? response.data : [];
      }
      
      // Ensure userData is an array before filtering
      if (!Array.isArray(userData)) {
        console.error('Expected array of users but got:', userData);
        userData = []; // Fallback to empty array if not an array
      }
        
      // Filter users with company_user role
      const companyUsers = userData.filter(user => user.role === 'company_user');
      setUsers(companyUsers);
    } catch (err) {
      console.error('Error loading users:', err);
      setError('Failed to load users. Please try again.');
    }
  }, [isAdmin]);

  // Fetch all customers for the company
  const loadCustomers = useCallback(async () => {
    try {
      const response = await customerService.getAllCustomers();
      setCustomers(response.data);
    } catch (err) {
      console.error('Error loading customers:', err);
      setError('Failed to load customers. Please try again.');
    }
  }, []);

  // Fetch all access permissions
  const loadAccessPermissions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await customerAccessService.getAllAccessPermissions();
      setAccessPermissions(response.data);
    } catch (err) {
      console.error('Error loading access permissions:', err);
      setError('Failed to load access permissions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Load data on component mount
  useEffect(() => {
    loadUsers();
    loadCustomers();
    loadAccessPermissions();
  }, [loadUsers, loadCustomers, loadAccessPermissions]);

  // Load access permissions for a specific user
  const loadUserAccessPermissions = async (userId) => {
    setLoading(true);
    try {
      const response = await customerAccessService.getUserAccessPermissions(userId);
      // We don't set the full accessPermissions here, just display in the user view
      setSelectedAccessUser({
        ...users.find(user => user._id === userId),
        accessPermissions: response.data
      });
    } catch (err) {
      console.error('Error loading user access permissions:', err);
      setError('Failed to load user access permissions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle user selection
  const handleUserSelect = (userId) => {
    if (userId) {
      setSelectedUser(users.find(user => user._id === userId));
      loadUserAccessPermissions(userId);
    } else {
      setSelectedUser(null);
      setSelectedAccessUser(null);
    }
  };

  // Grant access to a single customer
  const handleGrantAccess = async () => {
    if (!selectedUser || !selectedCustomers.length) {
      setError('Please select a user and at least one customer');
      return;
    }

    setLoading(true);
    try {
      // For single customer
      if (selectedCustomers.length === 1) {
        await customerAccessService.grantAccess(selectedUser._id, selectedCustomers[0]._id);
      } 
      // For multiple customers
      else {
        const customerIds = selectedCustomers.map(c => c._id);
        await customerAccessService.bulkGrantAccess(selectedUser._id, customerIds);
      }
      
      // Reload data
      loadAccessPermissions();
      if (selectedAccessUser) {
        loadUserAccessPermissions(selectedUser._id);
      }
      
      // Reset selections and close dialog
      setSelectedCustomers([]);
      setShowGrantDialog(false);
      setShowBulkGrantDialog(false);
      setSuccess(`Access granted successfully to ${selectedCustomers.length} customer(s)`);
    } catch (err) {
      console.error('Error granting access:', err);
      setError('Failed to grant access. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show confirmation dialog before revoking access
  const handleRevokeAccessConfirm = (accessId, customerName) => {
    setRevokeConfirmDialog({
      open: true,
      accessId,
      customerName: customerName || 'this customer'
    });
  };

  // Revoke access after confirmation
  const confirmRevokeAccess = async () => {
    const { accessId } = revokeConfirmDialog;
    
    if (!accessId) return;
    
    setLoading(true);
    try {
      await customerAccessService.revokeAccess(accessId);
      
      // Reload data
      loadAccessPermissions();
      if (selectedAccessUser) {
        loadUserAccessPermissions(selectedAccessUser._id);
      }
      
      setSuccess('Access revoked successfully');
      
      // Close confirmation dialog
      setRevokeConfirmDialog({
        open: false,
        accessId: null,
        customerName: ''
      });
    } catch (err) {
      console.error('Error revoking access:', err);
      setError('Failed to revoke access. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Update the original function to show confirmation instead
  const handleRevokeAccess = (accessId, customerName) => {
    handleRevokeAccessConfirm(accessId, customerName);
  };

  // Reset error and success messages
  const handleCloseAlert = () => {
    setError(null);
    setSuccess(null);
  };

  // Helper to find customer name by ID
  const getCustomerName = (customerId) => {
    const customer = customers.find(c => c._id === customerId);
    return customer ? customer.name : 'Unknown Customer';
  };

  // Helper to find user name by ID
  const getUserName = (userId) => {
    const user = users.find(u => u._id === userId);
    return user ? user.name : 'Unknown User';
  };

  return (
    <Box sx={{ px: { xs: 2, md: 3 } }}>
      <Typography variant="h5" component="h2" gutterBottom>
        Customer Access Management
      </Typography>
      <Typography variant="body1" paragraph>
        Manage which users can access specific customers. Only company users need explicit access.
        Company admins automatically have access to all customers in their company.
      </Typography>

      {/* Error and Success alerts */}
      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      {/* Main content grid */}
      <Grid container spacing={3}>
        {/* User selection */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Select User
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="user-select-label">Company User</InputLabel>
              <Select
                labelId="user-select-label"
                value={selectedUser?._id || ''}
                label="Company User"
                onChange={(e) => handleUserSelect(e.target.value)}
              >
                <MenuItem value="">
                  <em>Select a user</em>
                </MenuItem>
                {users.map(user => (
                  <MenuItem key={user._id} value={user._id}>
                    {user.name} ({user.email})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setShowGrantDialog(true)}
              disabled={!selectedUser}
              fullWidth
              sx={{ mb: 1 }}
            >
              Grant Access
            </Button>

            <Button
              variant="outlined"
              startIcon={<BusinessIcon />}
              onClick={() => setShowBulkGrantDialog(true)}
              disabled={!selectedUser}
              fullWidth
            >
              Bulk Grant Access
            </Button>
          </Paper>
        </Grid>

        {/* User permissions */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, minHeight: 400 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
              </Box>
            ) : selectedAccessUser ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <PersonIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">
                    {selectedAccessUser.name}'s Customer Access
                  </Typography>
                </Box>
                
                <Divider sx={{ mb: 2 }} />
                
                {selectedAccessUser.accessPermissions?.length > 0 ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Customer</TableCell>
                          <TableCell>Granted By</TableCell>
                          <TableCell>Granted On</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedAccessUser.accessPermissions.map((access) => (
                          <TableRow key={access._id}>
                            <TableCell>
                              {access.customer.name || getCustomerName(access.customer)}
                            </TableCell>
                            <TableCell>
                              {access.grantedBy?.name || getUserName(access.grantedBy)}
                            </TableCell>
                            <TableCell>
                              {new Date(access.grantedAt).toLocaleDateString()}
                            </TableCell>
                            <TableCell align="right">
                              <IconButton 
                                color="error" 
                                onClick={() => handleRevokeAccess(
                                  access._id, 
                                  access.customer.name || getCustomerName(access.customer)
                                )}
                                disabled={loading}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body1" color="text.secondary" align="center" sx={{ p: 4 }}>
                    This user doesn't have access to any customers.
                  </Typography>
                )}
              </>
            ) : (
              <Typography variant="body1" color="text.secondary" align="center" sx={{ p: 4 }}>
                Select a user to view or manage their customer access permissions.
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Grant Access Dialog */}
      <Dialog open={showGrantDialog} onClose={() => setShowGrantDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Grant Customer Access</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            Select a customer to grant access to {selectedUser?.name}
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Autocomplete
              options={customers}
              getOptionLabel={(option) => option.name}
              onChange={(event, newValue) => {
                setSelectedCustomers(newValue ? [newValue] : []);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select Customer" variant="outlined" />
              )}
              loading={loading}
              disabled={loading}
            />
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowGrantDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleGrantAccess} 
            variant="contained" 
            startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
            disabled={loading || !selectedCustomers.length}
          >
            Grant Access
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Grant Access Dialog */}
      <Dialog open={showBulkGrantDialog} onClose={() => setShowBulkGrantDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bulk Grant Customer Access</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            Select multiple customers to grant access to {selectedUser?.name}
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <Autocomplete
              multiple
              options={customers}
              getOptionLabel={(option) => option.name}
              onChange={(event, newValue) => {
                setSelectedCustomers(newValue);
              }}
              renderInput={(params) => (
                <TextField {...params} label="Select Customers" variant="outlined" />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.name}
                    {...getTagProps({ index })}
                    key={option._id}
                  />
                ))
              }
              loading={loading}
              disabled={loading}
            />
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            Selected: {selectedCustomers.length} customers
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBulkGrantDialog(false)} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleGrantAccess} 
            variant="contained" 
            startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
            disabled={loading || !selectedCustomers.length}
          >
            Grant Access to All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revoke Access Confirmation Dialog */}
      <Dialog 
        open={revokeConfirmDialog.open} 
        onClose={() => setRevokeConfirmDialog({ ...revokeConfirmDialog, open: false })}
      >
        <DialogTitle>Confirm Revoke Access</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to revoke {selectedAccessUser?.name}'s access to {revokeConfirmDialog.customerName}?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setRevokeConfirmDialog({ ...revokeConfirmDialog, open: false })}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmRevokeAccess} 
            color="error" 
            variant="contained"
            disabled={loading}
          >
            Revoke Access
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 